import { Component, Input } from '@angular/core';
import { MATERIAL_IMPORTS } from '../../../shared/material/material';
import { DatePipe, NgClass } from '@angular/common';
import { RegistroSignosVitales } from '../../../pages/pacientes/registro-signos-vitales/registro-signos-vitales';
import { Atencion, AtencionDashboard, Paciente } from '../../../pages/pacientes/pacientes';
import { MatDialog } from '@angular/material/dialog';
import { EventEmitter, Output } from '@angular/core';
import { GraficasSignosVitales } from '../../../pages/pacientes/registro-signos-vitales/graficas-signos-vitales/graficas-signos-vitales';
import Swal from 'sweetalert2';
import { EstadoAtencionService } from '../../../core/services/estado-anterior.service';
import { AtencionService } from '../../../core/services/atencion.service';
import { InicioAtencionService } from '../inicio-atencion.service';
import { AuthService } from '../../../core/services/auth.service';

@Component({
  standalone: true,
  selector: 'app-dashboard-detail',
  imports: [...MATERIAL_IMPORTS, NgClass, DatePipe],
  templateUrl: './dashboard-detail.html',
  styleUrl: './dashboard-detail.scss',
})
export class dashboadDetail {
  constructor(
    private dialog: MatDialog,
    private atencionService: AtencionService,
    private estadoAtencionService: EstadoAtencionService,
    private inicioAtencionService: InicioAtencionService,
    private authService: AuthService,
  ) {}

  @Input() pendientes: AtencionDashboard[] = [];

  @Input() totalPendientes = 0;

  @Output() verTodos = new EventEmitter<void>();

  @Output() consultaCancelada = new EventEmitter<void>();

  mostrarTodos() {
    this.verTodos.emit();
  }

  get pendientesOrdenados(): AtencionDashboard[] {
    const ordenPrioridad: Record<string, number> = {
      Emergencia: 1,
      UrgenteMayor: 2,
      Urgente: 3,
      UrgenciaMenor: 4,
      SinUrgencia: 5,

      rojo: 1,
      naranja: 2,
      amarillo: 3,
      verde: 4,
      azul: 5,
    };

    return [...this.pendientes]
      .sort((a, b) => {
        // Primero compara prioridad
        const prioridadA = ordenPrioridad[a.prioridad] ?? 999;
        const prioridadB = ordenPrioridad[b.prioridad] ?? 999;

        if (prioridadA !== prioridadB) {
          return prioridadA - prioridadB;
        }

        return this.convertirFecha(a.horaLlegada) - this.convertirFecha(b.horaLlegada);
      })
      .slice(0, 5);
  }

  convertirFecha(fecha: Date | string | null): number {
    if (!fecha) {
      return Number.MAX_SAFE_INTEGER;
    }

    return new Date(fecha).getTime();
  }

  // =====================================================
  // ABRIR REGISTRO DE SIGNOS VITALES
  // =====================================================

  abrirRegistroSignosVitales(atencion: Atencion) {
    // console.log('========== ATENCIÓN SELECCIONADA ==========');

    // console.log(atencion);

    const dialogRef = this.dialog.open(RegistroSignosVitales, {
      width: '95vw',
      maxWidth: '95vw',
      height: '90vh',
      data: atencion.paciente,
    });

    dialogRef.afterClosed().subscribe((pacienteActualizado) => {
      if (pacienteActualizado) {
        // console.log('Paciente actualizado:', pacienteActualizado);
        // Emitimos un evento para que
        // el componente padre recargue
        // la información.
      }
    });
  }

  abrirGragicaSignosVitales(atencion: Atencion) {
    this.dialog.open(GraficasSignosVitales, {
      width: '95vw',
      height: '90vh',
      maxWidth: '95vw',
      data: atencion.paciente,
    });
  }

  abrirConsulta(atencion: Atencion): void {
    const usuario = this.authService.getUser();
    const usuarioId = usuario?.id;

    if (!usuarioId) {
      Swal.fire({
        icon: 'error',
        title: 'Sesión no válida',
        text: 'No se pudo identificar al médico que inició sesión.',
        showConfirmButton: false,
        timer: 2000,
      });
      return;
    }

    // Consultar las atenciones que están actualmente en curso.
    this.atencionService.obtenerAtencionesAtendidos().subscribe({
      next: (atenciones: Atencion[]) => {
        const consultaActiva = atenciones.find(
          (actual) =>
            actual.estado === 'EN_ATENCION' &&
            String(actual.medico?.id) === String(usuarioId) &&
            String(actual.id) !== String(atencion.id),
        );

        // Si el médico no tiene otra consulta activa,
        // abrir directamente la atención seleccionada.
        if (!consultaActiva) {
          this.inicioAtencionService.abrirConsulta(atencion);
          return;
        }

        // Si ya tiene una consulta activa, pedir confirmación.
        const nombrePaciente = [
          consultaActiva.paciente?.nombre,
          consultaActiva.paciente?.apellido,
          consultaActiva.paciente?.segundoApellido,
        ]
          .filter(Boolean)
          .join(' ');

        Swal.fire({
          title: '¿Iniciar otra consulta?',
          text: nombrePaciente
            ? `Actualmente tienes una consulta activa con ${nombrePaciente}. Si continúas, la consulta anterior se finalizará y comenzarás a atender a este paciente.`
            : 'Ya tienes una consulta activa. Si continúas, la consulta anterior se finalizará y comenzarás a atender a este paciente.',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Sí, finalizar y continuar',
          cancelButtonText: 'No, volver',
          confirmButtonColor: '#24575A',
          cancelButtonColor: '#777',
          reverseButtons: true,
          focusCancel: true,
        }).then((resultado) => {
          if (!resultado.isConfirmed) {
            return;
          }

          this.inicioAtencionService.abrirConsulta(atencion);
        });
      },

      error: (error) => {
        // console.error('Error al comprobar las consultas activas:', error);

        Swal.fire({
          icon: 'error',
          title: 'No se pudo verificar la consulta',
          text: 'No fue posible comprobar si tienes otra consulta activa. Inténtalo nuevamente.',
          showConfirmButton: false,
          timer: 2000,
        });
      },
    });
  }

  cancelarConsulta(atencion: Atencion): void {
    if (!atencion.id) {
      // console.error('No se puede cancelar: falta el ID de la atención.');
      return;
    }

    Swal.fire({
      title: '¿Cancelar consulta?',
      text: `Se cancelará la atención de ${atencion.paciente?.nombre} ${atencion.paciente?.apellido ?? ''}.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, continuar',
      confirmButtonColor: '#24575A',
      cancelButtonText: 'Volver',
      cancelButtonColor: '#777',
      reverseButtons: true,
    }).then((resultado) => {
      if (!resultado.isConfirmed) {
        return;
      }

      // Pedir motivo de cancelación
      Swal.fire({
        title: 'Motivo de cancelación',
        input: 'textarea',
        inputLabel: 'Indica el motivo por el que se cancela la consulta',
        inputPlaceholder: 'Escribe el motivo...',
        inputAttributes: {
          'aria-label': 'Motivo de cancelación',
        },
        showCancelButton: true,
        confirmButtonText: 'Cancelar consulta',
        confirmButtonColor: '#24575A',
        cancelButtonText: 'Volver',
        cancelButtonColor: '#777',
        reverseButtons: true,
        inputValidator: (value) => {
          if (!value || !value.trim()) {
            return 'Debes indicar un motivo de cancelación.';
          }

          return null;
        },
      }).then((resultadoMotivo) => {
        if (!resultadoMotivo.isConfirmed) {
          return;
        }

        const motivo = resultadoMotivo.value.trim();

        this.atencionService.cancelarAtencion(atencion.id!, motivo).subscribe({
          next: () => {
            Swal.fire({
              icon: 'success',
              title: 'Consulta cancelada',
              text: 'La atención se canceló correctamente.',
              showConfirmButton: false,
              timer: 1500,
            });

            this.consultaCancelada.emit();
          },

          error: (error) => {
            // console.error('Error al cancelar atención:', error);

            if (error?.status === 409) {
              Swal.fire({
                icon: 'info',
                title: 'No se puede cancelar',
                text: error?.error?.message ?? 'Esta atención ya no está en espera.',
                showConfirmButton: false,
                timer: 2000,
              });

              // Aunque no se pudo cancelar, refrescamos
              // para actualizar el estado en el frontend.
              this.consultaCancelada.emit();
            } else {
              Swal.fire({
                icon: 'error',
                title: 'No se pudo cancelar',
                text: error?.error?.message ?? 'Ocurrió un error al cancelar la atención.',
                showConfirmButton: false,
                timer: 2000,
              });
            }
          },
        });
      });
    });
  }
}
